import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormFeedback,
  Container,
  Alert,
  Button,
} from "reactstrap";
import { useFormik, FormikProvider } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import {
  createWorkOrder,
  getWorkOrder,
  updateWorkOrder,
} from "features/WorkOrders/services/WorkOrderService";
import { WorkOrder } from "features/WorkOrders/types/work-order.types";
import {
  boqRowToWorkOrderFormRow,
  createEmptyAdhocWorkOrderFormRow,
  formWorkOrderItemRowToPersistPayload,
  mapApiWorkOrderItemToFormRow,
  mapQuotationItemToWorkOrderBoqRow,
  workOrderFormRowToBoqRow,
} from "features/WorkOrders/utils/work-order-item.utils";
import BoqItemsFieldArray from "components/common/BoqItemsFieldArray";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import {
  getQuotation,
  getQuotationOptions,
} from "features/Quotations/services/QuotationService";
import {
  QuotationOption,
  QuotationSingleValue,
} from "features/Quotations/components/QuotationSelectComponents";
import {
  WorkOrderSourceType,
  WorkOrderStatus,
  workOrderStatusMap,
} from "features/WorkOrders/constants/work-order.constants";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";
import { workOrderPermissionFlags } from "features/WorkOrders/utils/workOrderPermissions";
import type {
  WorkOrderExpensePayload,
  WorkOrderSubcontractFormPayload,
} from "features/WorkOrders/types/work-order.types";
import WorkOrderExpensesFieldArray from "./WorkOrderExpensesFieldArray";
import WorkOrderSubcontractsFieldArray from "./WorkOrderSubcontractsFieldArray";
import { useProfile } from "features/Authentication/hooks/useProfile";
import AppFlatpickr from "components/common/AppFlatpickr";
import moment from "moment";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

const errMessage = (err: unknown) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? "Something went wrong.";

function emptyWorkOrderBoqRow() {
  return workOrderFormRowToBoqRow(createEmptyAdhocWorkOrderFormRow());
}

const WorkOrderForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(
    !isEdit ? "New Work Order" : "Edit Work Order",
  );

  const { id } = useParams();
  const navigate = useNavigate();
  const { user: accessUser, isSuperAdmin } = useProfile();

  const [record, setRecord] = useState<WorkOrder>();
  const [quotationTotal, setQuotationTotal] = useState<number | null>(null);
  const lastHydratedQuotationIdRef = useRef<number | null>(null);

  const woFlags = useMemo(
    () => workOrderPermissionFlags(accessUser, record ?? null, isSuperAdmin),
    [accessUser, record, isSuperAdmin],
  );
  const canEditLoadedRecord = woFlags.canEdit;

  const formReadOnly = isEdit && Boolean(record) && !canEditLoadedRecord;

  const canEditLineItems = woFlags.canEditLineItems;
  const boqReadOnly = isEdit && Boolean(record) && !canEditLineItems;

  const agreedAmountLocked =
    isEdit && Boolean(record) && record?.status !== WorkOrderStatus.NEW;

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: record?.title ?? "",
      sourceType: record?.sourceType ?? WorkOrderSourceType.QUOTATION,
      description: record?.description ?? "",
      sourceId: record?.sourceId ?? "",
      items:
        (record?.items ?? []).length > 0
          ? (record?.items ?? []).map((it) =>
              workOrderFormRowToBoqRow(mapApiWorkOrderItemToFormRow(it)),
            )
          : [emptyWorkOrderBoqRow()],
      expenses: (record?.expenses ?? []).map((e) => ({
        id: e.id,
        expenseCategoryId: e.expenseCategoryId,
        amount: e.amount,
        description: e.description ?? "",
        _categoryLabel: e.category?.name ?? "",
      })),
      subcontracts: (record?.subcontracts ?? []).map((sc) => ({
        id: sc.id,
        supplierId: sc.supplierId,
        _supplierLabel: sc.supplier?.name ?? "",
        description: sc.description,
        agreedAmount: sc.agreedAmount,
        finalAmount: sc.finalAmount != null ? String(sc.finalAmount) : "",
        labourCount: sc.labourCount != null ? String(sc.labourCount) : "",
        notes: sc.notes ?? "",
        status: sc.status,
      })),
      agreedAmount: record?.agreedAmount ?? "",
      code: record?.code ?? "",
      plannedStartDate: record?.plannedStartDate ?? "",
      plannedEndDate: record?.plannedEndDate ?? "",
      isEdit,
      status: record?.status ?? "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const { sourceType } = values;

      const expenseLines: WorkOrderExpensePayload[] = (values.expenses ?? [])
        .filter((e: any) => e.expenseCategoryId && e.amount != null)
        .map((e: any) => ({
          ...(e.id != null ? { id: e.id } : {}),
          expenseCategoryId: Number(e.expenseCategoryId),
          amount: Number(e.amount),
          description: e.description?.trim() || null,
        }));

      const subcontractLines: WorkOrderSubcontractFormPayload[] = (
        values.subcontracts ?? []
      )
        .filter(
          (sc: any) =>
            sc.supplierId && sc.description?.trim() && sc.agreedAmount != null,
        )
        .map((sc: any) => ({
          ...(sc.id != null ? { id: sc.id } : {}),
          supplierId: Number(sc.supplierId),
          description: sc.description.trim(),
          agreedAmount: Number(sc.agreedAmount),
          finalAmount:
            sc.finalAmount !== "" && sc.finalAmount != null
              ? Number(sc.finalAmount)
              : null,
          labourCount:
            sc.labourCount !== "" && sc.labourCount != null
              ? Number(sc.labourCount)
              : null,
          notes: sc.notes?.trim() || null,
        }));

      const parseAmount = (v: any): number | null => {
        if (v === "" || v == null) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const financialFields = {
        agreedAmount: parseAmount(values.agreedAmount),
      };

      const codeValue = values.code?.trim() || undefined;

      const plannedDates = {
        plannedStartDate: values.plannedStartDate?.trim() || null,
        plannedEndDate: values.plannedEndDate?.trim() || null,
      };

      const updatePayload = {
        sourceId: Number(values.sourceId),
        description: values.description,
        title: values.title,
        expenses: expenseLines,
        subcontracts: subcontractLines,
        ...financialFields,
        ...plannedDates,
        ...(codeValue !== undefined ? { code: codeValue } : {}),
      };

      const createPayload = {
        sourceId: Number(values.sourceId),
        sourceType: sourceType as WorkOrderSourceType,
        description: values.description,
        title: values.title,
        expenses: expenseLines,
        subcontracts: subcontractLines,
        ...financialFields,
        ...plannedDates,
        ...(codeValue !== undefined ? { code: codeValue } : {}),
      };

      const woFormRows = (values.items ?? []).map(boqRowToWorkOrderFormRow);
      const items = woFormRows.map(formWorkOrderItemRowToPersistPayload);

      try {
        let response;
        if (isEdit) {
          response = await updateWorkOrder(Number(id), {
            ...updatePayload,
            items,
          });
        } else {
          response = await createWorkOrder({ ...createPayload, items });
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        } else {
          toast.error(response.message);
        }
      } catch (err) {
        toast.error(errMessage(err));
      }
    },
  });

  const selectedSourceId = validation.values.sourceId;
  const setWorkOrderFieldValue = validation.setFieldValue;

  useEffect(() => {
    if (isEdit) return;

    const sourceId = Number(selectedSourceId);
    if (!Number.isFinite(sourceId) || sourceId <= 0) return;
    if (lastHydratedQuotationIdRef.current === sourceId) return;

    let cancelled = false;
    void (async () => {
      try {
        const response = await getQuotation(sourceId);
        if (cancelled) return;
        if (!response.success || !response.data) {
          toast.error(response.message ?? "Could not load quotation items.");
          return;
        }
        const mapped =
          response.data.items?.map(mapQuotationItemToWorkOrderBoqRow) ?? [];
        setWorkOrderFieldValue(
          "items",
          mapped.length ? mapped : [emptyWorkOrderBoqRow()],
        );
        setQuotationTotal(response.data.total ?? null);
        lastHydratedQuotationIdRef.current = sourceId;
      } catch (err) {
        if (!cancelled) {
          toast.error(errMessage(err));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, selectedSourceId, setWorkOrderFieldValue]);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await getWorkOrder(Number(id));
        const { success, data } = response;
        if (success) {
          setRecord(data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  useEffect(() => {
    if (!isEdit) return;
    const sourceId = record?.sourceId;
    if (sourceId == null || !Number.isFinite(Number(sourceId))) return;

    let cancelled = false;
    void (async () => {
      try {
        const response = await getQuotation(Number(sourceId));
        if (!cancelled && response.success && response.data) {
          setQuotationTotal(response.data.total ?? null);
        }
      } catch (err) {
        console.warn(err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, record?.sourceId]);

  useEffect(() => {
    if (isEdit || quotationTotal == null) return;
    const current = validation.values.agreedAmount;
    if (current === "" || current == null) {
      setWorkOrderFieldValue("agreedAmount", quotationTotal);
    }
    // Only re-run when the fetched total changes — must not depend on
    // agreedAmount itself, or every keystroke would re-trigger this check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, quotationTotal]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Work Order" : "New Work Order"}
            pageTitle="Work Orders"
            items={[{ label: "Work Orders", path: "/work-orders" }]}
          />
          <FormikProvider value={validation}>
            <Form
              className="needs-validation"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Row className="justify-content-center">
                <Col>
                  <Card className="shadow-none border">
                    <CardHeader className="border-bottom bg-transparent pb-3">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h5 className="card-title mb-0">
                          {isEdit ? "Edit work order" : "Create work order"}
                        </h5>
                        {record ? (
                          <StatusBadge
                            value={record.status}
                            map={workOrderStatusMap}
                          />
                        ) : null}
                      </div>
                      <p className="text-muted mb-0 small">
                        Give this work order a title and description, link the
                        source quotation, set the agreed amount and schedule,
                        then add line items, expenses, and subcontracts.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      {isEdit && record && !canEditLoadedRecord ? (
                        <Alert color="warning" className="mb-4">
                          You can view this work order, but only the owner or
                          an administrator can change it.
                        </Alert>
                      ) : null}
                      {isEdit && formReadOnly && !canEditLineItems ? (
                        <Alert color="secondary" className="mb-4">
                          This work order is cancelled. Nothing can be changed.
                        </Alert>
                      ) : isEdit && formReadOnly ? (
                        <Alert color="secondary" className="mb-4">
                          This work order is ready for billing or completed —
                          other details are locked, but line items below stay
                          editable so you can correct them and regenerate the
                          invoice.
                        </Alert>
                      ) : null}

                      <fieldset
                        disabled={formReadOnly}
                        className="border-0 p-0 m-0"
                        style={{ minWidth: 0 }}
                      >
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Details
                        </div>
                        <Row className="g-3 mb-2">
                          <Col md={4}>
                            <Label htmlFor="code" className="form-label">
                              Work order code{" "}
                              <span className="text-muted fw-normal">
                                (optional)
                              </span>
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              placeholder="Auto-generated if blank"
                              id="code"
                              name="code"
                              readOnly={isEdit && agreedAmountLocked}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.code || ""}
                              invalid={
                                !!(
                                  validation.touched.code &&
                                  validation.errors.code
                                )
                              }
                            />
                            {isEdit && agreedAmountLocked ? (
                              <small className="text-muted">
                                Code can only be set while the work order is in
                                New status.
                              </small>
                            ) : null}
                            {validation.touched.code &&
                            validation.errors.code ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.code}</div>
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={12}>
                            <Label htmlFor="title" className="form-label">
                              Title
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              placeholder="Enter title"
                              id="title"
                              name="title"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.title || ""}
                              invalid={
                                validation.touched.title &&
                                validation.errors.title
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.title &&
                            validation.errors.title ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.title}</div>
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={12}>
                            <Label htmlFor="description" className="form-label">
                              Description
                            </Label>
                            <Input
                              type="textarea"
                              className="form-control"
                              id="description"
                              rows="3"
                              name="description"
                              placeholder="Enter description"
                              value={validation.values.description || ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.description &&
                                validation.errors.description
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.description &&
                            validation.errors.description ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.description}</div>
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>

                        <hr className="my-4 text-muted opacity-25" />

                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Source
                        </div>
                        <Row className="mb-2">
                          <Col md={6}>
                            <Label className="form-label">Quotation</Label>
                            <p className="text-muted small mb-2">
                              Only quotations without an active work order are
                              listed.
                            </p>
                            <AsyncPaginatedSelect
                              key="quotation-select"
                              defaultOption={
                                record?.source && {
                                  label: record?.source.code,
                                  value: record?.source.id,
                                  title: record?.source.title,
                                  status: record?.source.status,
                                }
                              }
                              fetchOptions={getQuotationOptions}
                              components={{
                                Option: QuotationOption,
                                SingleValue: QuotationSingleValue,
                              }}
                              placeholder="Search Quotations..."
                              value={validation.values.sourceId}
                              onChange={(val: any) => {
                                validation.setFieldTouched("sourceId", true);
                                validation.setFieldValue(
                                  "sourceId",
                                  val?.value,
                                );
                              }}
                              onBlur={() =>
                                validation.setFieldTouched("sourceId", true)
                              }
                              isInvalid={
                                !!(
                                  (validation.touched.sourceId ||
                                    validation.submitCount > 0) &&
                                  validation.errors.sourceId
                                )
                              }
                              isDisabled={isEdit}
                            />
                            {(validation.touched.sourceId ||
                              validation.submitCount > 0) &&
                              validation.errors.sourceId && (
                                <FormFeedback type="invalid">
                                  {validation.errors.sourceId}
                                </FormFeedback>
                              )}
                          </Col>
                        </Row>

                        <hr className="my-4 text-muted opacity-25" />

                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Financials
                        </div>
                        <Row className="g-3 mb-2">
                          <Col md={6}>
                            <Label className="form-label">Agreed amount</Label>
                            <div className="input-group">
                              <span className="input-group-text text-muted">
                                <i className="ri-shake-hands-line" />
                              </span>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                name="agreedAmount"
                                placeholder="0.00"
                                readOnly={agreedAmountLocked}
                                value={validation.values.agreedAmount ?? ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  !agreedAmountLocked &&
                                  !!(
                                    validation.touched.agreedAmount &&
                                    validation.errors.agreedAmount
                                  )
                                }
                              />
                              {!agreedAmountLocked &&
                              validation.touched.agreedAmount &&
                              validation.errors.agreedAmount ? (
                                <FormFeedback>
                                  {validation.errors.agreedAmount}
                                </FormFeedback>
                              ) : null}
                            </div>
                            <small className="text-muted">
                              Negotiated with client before work begins
                              {quotationTotal != null ? (
                                <>
                                  {" "}
                                  — quotation total:{" "}
                                  <span className="text-body fw-medium">
                                    {formatCurrency(quotationTotal)}
                                  </span>
                                </>
                              ) : null}
                            </small>
                            {!agreedAmountLocked ? (
                              <small className="text-muted d-block mt-1">
                                Reducing this value below the total of any
                                active client advances already collected against
                                this work order will be blocked.
                              </small>
                            ) : null}
                            {isEdit && record ? (
                              <LinkedClientAdvancesTable
                                className="mt-3"
                                sourceType={ClientAdvanceSourceType.WORK_ORDER}
                                sourceId={record.id}
                              />
                            ) : null}
                          </Col>
                        </Row>

                        <hr className="my-4 text-muted opacity-25" />

                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Schedule
                        </div>
                        <Row className="g-3 mb-2">
                          <Col md={6}>
                            <Label
                              htmlFor="plannedStartDate"
                              className="form-label"
                            >
                              Planned start date
                            </Label>
                            <AppFlatpickr
                              name="plannedStartDate"
                              className={`form-control${validation.touched.plannedStartDate && validation.errors.plannedStartDate ? " is-invalid" : ""}`}
                              placeholder="Select start date"
                              options={{ dateFormat: "d M, Y" }}
                              value={
                                validation.values.plannedStartDate
                                  ? new Date(validation.values.plannedStartDate)
                                  : ""
                              }
                              onChange={(date: Date[]) =>
                                validation.setFieldValue(
                                  "plannedStartDate",
                                  date[0]
                                    ? moment(date[0]).format("YYYY-MM-DD")
                                    : "",
                                )
                              }
                            />
                            {validation.touched.plannedStartDate &&
                            validation.errors.plannedStartDate ? (
                              <FormFeedback>
                                {validation.errors.plannedStartDate}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <Label
                              htmlFor="plannedEndDate"
                              className="form-label"
                            >
                              Planned end date
                            </Label>
                            <AppFlatpickr
                              name="plannedEndDate"
                              className={`form-control${validation.touched.plannedEndDate && validation.errors.plannedEndDate ? " is-invalid" : ""}`}
                              placeholder="Select end date"
                              options={{ dateFormat: "d M, Y" }}
                              value={
                                validation.values.plannedEndDate
                                  ? new Date(validation.values.plannedEndDate)
                                  : ""
                              }
                              onChange={(date: Date[]) => {
                                void validation.setFieldValue(
                                  "plannedEndDate",
                                  date[0]
                                    ? moment(date[0]).format("YYYY-MM-DD")
                                    : "",
                                );
                                void validation.setFieldTouched(
                                  "plannedEndDate",
                                  true,
                                );
                              }}
                            />
                            {validation.touched.plannedEndDate &&
                            validation.errors.plannedEndDate ? (
                              <FormFeedback>
                                {validation.errors.plannedEndDate}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>

                        <hr className="my-4 text-muted opacity-25" />

                        <WorkOrderSubcontractsFieldArray
                          validation={validation}
                          readOnly={formReadOnly}
                        />

                        <hr className="my-4 text-muted opacity-25" />

                        <WorkOrderExpensesFieldArray
                          validation={validation}
                          readOnly={formReadOnly}
                        />
                      </fieldset>

                      <hr className="my-4 text-muted opacity-25" />

                      {isEdit && boqReadOnly ? (
                        <Alert color="secondary" className="mb-4">
                          This work order is cancelled. Line items cannot be
                          changed.
                        </Alert>
                      ) : null}

                      <fieldset
                        disabled={boqReadOnly}
                        className="border-0 p-0 m-0"
                        style={{ minWidth: 0 }}
                      >
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                          Bill of quantities
                        </div>
                        <p className="text-muted small mb-3">
                          Materials, labour, and services — description, unit,
                          quantity, and rate (amount is quantity × rate).
                        </p>
                        <div className="border rounded p-3 bg-light bg-opacity-25">
                          <BoqItemsFieldArray
                            validation={validation}
                            readOnly={boqReadOnly}
                            addLineLabel="Add line item"
                            getDefaultRow={emptyWorkOrderBoqRow}
                            embedded
                          />
                        </div>
                      </fieldset>
                    </CardBody>
                    <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                      <BackButton />
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={
                          (formReadOnly && boqReadOnly) ||
                          validation.isSubmitting
                        }
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            {isEdit ? "Updating…" : "Submitting…"}
                          </>
                        ) : isEdit ? (
                          "Save changes"
                        ) : (
                          "Create work order"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Form>
          </FormikProvider>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default WorkOrderForm;
